import type {ClientStatusPublic} from '@wix/cashier-common/dist/src/types/PaymentStatus'
import {poll, waitALittle} from '@wix/panda-js-utils'
import {
  EVENTS_APP_ID,
  EVENTS_DETAILS_PAGE_ID,
  ExperimentNames,
  PlanInfo,
  getCouponDiscount,
  getCouponDiscountCode,
  getOrderGiftCardsTotalAmount,
  getValidGiftCardDetails,
  hasSeatingPlan,
} from '@wix/wix-events-commons-statics'
import {ILocation, NavOptions} from '@wix/yoshi-flow-editor'
import {getLocale} from '../../../../commons/selectors/environment'
import {isMembersInstalled} from '../../../../commons/selectors/installed-apps'
import {validateToken} from '../../../../commons/utils/validate-token'
import {updatePlace} from '../reducers/seating/places'
import {getCurrentMemberDetails} from '../selectors/current-member-details'
import {getEvent} from '../selectors/event'
import {getCouponCode, getGiftCardCode} from '../selectors/invoice'
import {getNavigationQuery, getPaymentRedirectUrl} from '../selectors/navigation'
import {getSelectedPaidPlan} from '../selectors/paid-plans'
import {isRequestPending} from '../selectors/pending-requests'
import {getInvoice, isPreparingOrder} from '../selectors/placed-order'
import {isPlaceArea, pricingOptionPlaceId} from '../selectors/seating/place'
import {getAnyPlaceById, getPlaces} from '../selectors/seating/places'
import {Interaction} from '../services/logger'
import {createAsyncAction} from '../services/redux-toolkit'
import {RegFormData} from '../types/registration'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {extractFormData} from '../utils/api-data-mapper'
import {applyCoupon} from './coupon'
import {applyGiftCard} from './gift-card'
import {navigateTo, navigateToLinkExpired, navigateToNotFound} from './navigation'
import {applyPlan} from './paid-plans'
import {describeSeatingPlan} from './seating/plan'

export const placeOrder = createAsyncAction<
  {order: wix.events.ticketing.Order},
  {eventId: string; buyer: RegFormData; guests?: RegFormData[]}
>(
  'PLACE_ORDER',
  async ({eventId, buyer, guests}, {getState, extra: {wixCodeApi, logger, serverApi}}) => {
    const state = getState()

    logger.startInteraction(Interaction.Checkout)

    const couponCode = getCouponDiscountCode(getInvoice(state))
    const giftCardCode = getGiftCardCode(state)
    const memberData = getCurrentMemberDetails(state)
    const memberId = isMembersInstalled(state) && memberData ? memberData.id : undefined
    const selectedPlan = getSelectedPaidPlan(state) || ({} as PlanInfo)
    const {benefitId, planOrderId} = selectedPlan
    const paymentRedirectUrl = getPaymentRedirectUrl(state, wixCodeApi)

    const orderGuests = guests ? guests.map(extractFormData) : null

    const response = await serverApi.placeOrder({
      eventId,
      buyer,
      guests: orderGuests as any,
      couponCode,
      giftCardCode,
      memberId,
      planOrderId,
      benefitId,
      locale: getLocale(state),
      paymentRedirectUrl,
    })

    return response
  },
  {
    condition: (_, {getState}) => {
      const state = getState()
      return !isRequestPending(state, placeOrder.typePrefix)
    },
  },
)

export const postPlaceOrder =
  (order: wix.events.ticketing.Order) =>
  (dispatch: Function, getState: GetState, {logger}: StoreExtraArgs) => {
    logger.finishInteraction(Interaction.Checkout)
    const totalPrice = Number(order.totalPrice.amount) - getOrderGiftCardsTotalAmount(order.giftCardPaymentDetails)

    if (!totalPrice) {
      dispatch(navigateTo(getState().placedOrder.orderPageUrl))
    }
  }

interface UpdateOrderParams {
  buyer?: RegFormData
  guests?: RegFormData[]
  paymentDetailsId?: string
  paymentMethod?: string
}

export const updatePaymentMethod =
  ({paymentMethod}: {paymentMethod: string}) =>
  (dispatch: Function, getState: GetState) => {
    const state = getState()
    const buyer = state.checkout.buyerDetails
    const guests = state.checkout.ticketsDetails

    return dispatch(updateOrder({buyer, guests, paymentMethod})).unwrap()
  }

export const updateOrder = createAsyncAction<wix.events.ticketing.UpdateCheckoutResponse, UpdateOrderParams>(
  'UPDATE_ORDER',
  async ({buyer, guests, paymentDetailsId, paymentMethod}, {getState, extra: {serverApi}}) => {
    const state = getState()
    const eventId = getEvent(state).id
    const selectedPlan = getSelectedPaidPlan(state) || ({} as PlanInfo)
    const {benefitId, planOrderId} = selectedPlan

    const response = await serverApi.updateOrder({
      eventId,
      paymentDetailsId,
      paymentMethod,
      orderNumber: state.placedOrder.order.orderNumber,
      buyer: extractFormData(buyer) as unknown as RegFormData,
      guests: guests?.map(extractFormData) as unknown as RegFormData[],
      locale: getLocale(state),
      couponCode: getCouponCode(state),
      giftCardCode: getGiftCardCode(state),
      paidPlanBenefit: benefitId && planOrderId ? {benefitId, planOrderId} : undefined,
    })

    return response
  },
)

export const expressCheckout = createAsyncAction<{clientStatus: ClientStatusPublic}, {paymentDetailsId: string}>(
  'EXPRESS_CHECKOUT',
  async (params, {dispatch, extra: {flowAPI}, getState}) => {
    const state = getState()

    const [{confirmPayment}, updateOrderResponse] = await Promise.all([
      import('@wix/cashier-express-checkout-widget/dist/src/utils/confirmPayment'),
      dispatch(
        updateOrder({
          buyer: state.checkout.buyerDetails,
          guests: state.checkout.ticketsDetails,
          paymentDetailsId: params.paymentDetailsId,
        }),
      ).unwrap(),
    ])

    const {chargeToken, orderPageUrls} = updateOrderResponse

    const confirmPaymentResponse = await confirmPayment({
      meta: {
        appDefId: EVENTS_APP_ID,
        msid: flowAPI.controllerConfig.platformAPIs.bi?.metaSiteId!,
        visitorId: flowAPI.controllerConfig.platformAPIs?.bi?.visitorId!,
        widgetId: EVENTS_DETAILS_PAGE_ID,
      },
      biLoggerFactory: flowAPI.essentials.biLoggerFactory,
      chargeResponseToken: chargeToken,
      redirectFn: flowAPI.controllerConfig.wixCodeApi.location.to,
      newApi: true,
    })

    const location = await flowAPI.controllerConfig.platformApiProvider.getPlatformApi<ILocation>('location')
    const navOptions: NavOptions = {disableScrollToTop: true}

    switch (confirmPaymentResponse.clientStatus) {
      case 'Approved':
        location.to(orderPageUrls.success, navOptions)
        break
      case 'Pending':
      case 'InProcess':
        location.to(orderPageUrls.pending, navOptions)
        break
      case 'Failed':
      case 'Unknown':
      default:
        location.to(orderPageUrls.error, navOptions)
        break
    }

    return confirmPaymentResponse
  },
)

export const getCheckoutData = createAsyncAction<void, {eventId: string; reservationId: string; orderNumber?: string}>(
  'GET_CHECKOUT_DATA',
  async ({eventId, reservationId, orderNumber}, {extra: {serverApi, flowAPI}, dispatch, getState}) => {
    const checkoutSummaryFromReservationEnabled = flowAPI.experiments.enabled(
      ExperimentNames.CheckoutSummaryFromReservation,
    )

    const {order} = orderNumber ? await dispatch(getOrder({eventId, orderNumber})).unwrap() : {order: undefined}
    const withSeatingPlan = hasSeatingPlan(getEvent(getState()))
    const selectedPaidPlan = getSelectedPaidPlan(getState())

    if (withSeatingPlan && !checkoutSummaryFromReservationEnabled) {
      await dispatch(describeSeatingPlan())

      const {reservations} = await serverApi.getInvoice(eventId, reservationId)
      const state = getState()

      reservations.forEach(reservation => {
        reservation.ticketDetails.forEach(({seatId, capacity, pricingOptionId, priceOverride}) => {
          const place = getAnyPlaceById(getPlaces(state), seatId)
          const area = isPlaceArea(place)

          dispatch(
            updatePlace({
              place: {
                id: !!pricingOptionId && area ? pricingOptionPlaceId(seatId, pricingOptionId) : seatId,
                quantity: capacity,
                selectedPricingOptionIds: pricingOptionId
                  ? Array.from<string>({length: capacity}).fill(pricingOptionId)
                  : undefined,
                donation: priceOverride && !pricingOptionId ? priceOverride : undefined,
              },
            }),
          )
        })
      })
    }

    if (order) {
      const validGiftCardDetails = getValidGiftCardDetails(order.giftCardPaymentDetails)

      if (validGiftCardDetails.length) {
        dispatch(applyGiftCard(eventId, order.reservationId, validGiftCardDetails[0].code))
      }

      if (getCouponDiscount(order.invoice)) {
        dispatch(applyCoupon(eventId, order.reservationId, getCouponDiscountCode(order.invoice)))
      }
    }

    if (selectedPaidPlan) {
      dispatch(applyPlan({eventId, reservationId, planOrderId: selectedPaidPlan.planOrderId}))
    }
  },
)

export const getOrder = createAsyncAction<
  {order: wix.events.ticketing.Order; calendarLinks: any; dates: any},
  {eventId: string; orderNumber: string; token?: string}
>('GET_ORDER', async ({eventId, orderNumber, token = ''}, {extra: {serverApi, flowAPI}, dispatch}) => {
  try {
    if (token) {
      const response = await serverApi.getOrderByToken(token)
      return response
    }

    if (eventId && orderNumber) {
      const response = await serverApi.getOrder(eventId, orderNumber)
      return response
    }

    throw new Error(`Missing params to get order, eventId: ${eventId} orderNumber: ${orderNumber} token: ${token}`)
  } catch (error) {
    flowAPI.reportError(error)
    const responseStatus = error.response?.status

    if (responseStatus === 404) {
      dispatch(navigateToNotFound())
    } else if (responseStatus === 400) {
      dispatch(navigateToLinkExpired())
    } else {
      dispatch(navigateToNotFound())
    }
  }
})

export const pollOrder = createAsyncAction<wix.events.ticketing.Order>(
  'POLL_ORDER',
  async (_, {extra: {serverApi, flowAPI}, getState}) => {
    const state = getState()
    const {token} = state.navigation?.query ?? {}

    const {tokenValid, errorMessage} = validateToken(token)

    if (!tokenValid) {
      flowAPI.reportError(new Error(errorMessage))
    }

    let response
    await poll({
      callback: async () => {
        response = await serverApi.getOrderByToken(token)

        return !isPreparingOrder(response.order)
      },
      intervalMs: 2000,
      totalMs: 15000,
    })
    return response.order
  },
)

export const getOrderTicketsDownloadUrl = createAsyncAction<
  {loadingTicketsUrl: boolean; downloadTicketsUrl?: string},
  number
>('GET_ORDER_TICKETS_DOWNLOAD_URL', async (retryLimit, {dispatch, getState, extra: {serverApi, flowAPI, logger}}) => {
  const {token} = getNavigationQuery(getState())
  const {tokenValid, errorMessage} = validateToken(token)

  if (!tokenValid) {
    flowAPI.reportError(new Error(errorMessage))
    await waitALittle()
    return {loadingTicketsUrl: false}
  }

  try {
    const response = await serverApi.getPapyrusTicketsDownloadUrl({token})
    if (response.ready && response.downloadUrl) {
      logger.finishInteraction(Interaction.LoadingTickets)
      return {loadingTicketsUrl: !response.ready, downloadTicketsUrl: response.downloadUrl}
    }
    if (retryLimit > 0) {
      await waitALittle()
      dispatch(getOrderTicketsDownloadUrl(--retryLimit))
      return {loadingTicketsUrl: true}
    }
    throw new Error(`Automatic retry limit exceeded, please retry by clicking download`)
  } catch (e) {
    flowAPI.reportError(e)

    return {loadingTicketsUrl: false}
  }
})
